import React from 'react';
import {createRoot} from 'react-dom/client';
import './index.css';
import './custom.d.ts';
import './FontAwesome';
import {App} from './App';
import * as Sentry from '@sentry/react';
import {browserTracingIntegration, replayIntegration} from '@sentry/react';
import './polyfill';
import {config} from './config';
import {analytics, isRestError, setConfig} from '@resurs-internal/rb-app-api';
import {
  getAnalytics,
  logEvent,
  setUserId,
  setUserProperties,
} from 'firebase/analytics';
import {firebaseApp} from './services/firebase/firebase';
import {initFullStory} from './services/fullstory';
import './localServiceOverride';
// init i18next
import './i18next-init';
// register the WebCrypto PBKDF2 provider for the 3DS static-password hash
import './services/pbkdf2';

// init sentry
Sentry.init({
  environment: config.env,
  beforeSend(event, hint) {
    const error = hint.originalException;
    // ignore non-exception errors
    if (!(error instanceof Error)) {
      return null;
    }

    if (isRestError(error)) {
      const status = error.status;
      // ignore unauthorized errors
      if (status === 401 || status === 403) {
        return null;
      }

      // ignore customer-service errors
      if (error.message.includes('CountryCode or customerId was null')) {
        return null;
      }

      event.tags = {...event.tags, status};
      return event;
    }
    return error;
  },
  dsn: config.sentryDSN,
  replaysOnErrorSampleRate: 1.0,
  integrations: [
    browserTracingIntegration(),
    // Block the same elements FullStory excludes so both replay tools share one privacy marker.
    replayIntegration({block: ['.fs-exclude']}),
  ],
  release: `mypages-client@${config.clientVersion}`,
  ignoreErrors: [
    'Non-Error exception captured',
    'Non-Error promise rejection captured',
    'Object captured as promise rejection with keys: revert, silent',
    // safari network error
    'TypeError: Load failed', // english
    'TypeError: avbruten', // swedish
    'Load failed',
    // chrome network error
    'TypeError: Failed to fetch',
    'Failed to fetch',
    // Google analytic init error
    "TypeError: undefined is not an object (evaluating 'a.L')",
  ],
});

// init rb-app-api
setConfig(config);

// init analytics
// check OneTrust cookie for analytics consent which is C0002
const isAnalyticsEnabled = () => {
  return (
    window as {OnetrustActiveGroups?: string}
  ).OnetrustActiveGroups?.includes?.('C0002');
};

// init FullStory session replay (gated on consent + feature flag)
initFullStory();

const firebaseAnalytics = getAnalytics(firebaseApp);
analytics.onEvent = (event, parameters) => {
  if (!isAnalyticsEnabled()) return;
  logEvent(firebaseAnalytics, event, parameters);
};
analytics.onUserInfo = (info) => {
  if (!isAnalyticsEnabled()) return;
  if (info.user_id) setUserId(firebaseAnalytics, info.user_id);
  setUserProperties(firebaseAnalytics, info);
};
analytics.setUserInfo({
  client: 'mypages',
  platform: 'web',
  screen_size: `${window.screenX}x${window.screenY}`,
  version: config.clientVersion,
});

const container = document.getElementById('root') as HTMLElement;
const root = createRoot(container);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
